import { Button, Icon, Typography } from '@smartgift/frontend-toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Card, CardContent, CardHeader } from '@hero/atoms';
import { DetailItem, OrderStatusBadge, Table } from '@hero/molecules';
import { ROUTE_PATHS } from '@hero/routes';
import { useGetRecognition, useGetThankYouNotes } from '@hero/services/order-service';
import { convertHtmlMessageToString, getSafeInnerHtml } from '@hero/utils';

import * as S from './style';

export const ThankYouNotesDetailTable = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { recognitionCode } = useParams();
  const [selectedNote, setSelectedNote] = useState(null);
  const [{ searchTerm }, setGlobalFilters] = useState({});
  const [searchParams] = useSearchParams();

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });

  const { data: recognitionData } = useGetRecognition(
    { recognitionCode },
    {
      enabled: !!recognitionCode
    }
  );

  const recognitionDBId = recognitionData?.id;
  const { data: thankYouNotesData, isLoading: thankYouNotesLoading } = useGetThankYouNotes(
    {
      recognitionId: recognitionDBId,
      searchTerm,
      page: pageIndex + 1,
      limit: pageSize
    },
    {
      enabled: !!recognitionDBId
    }
  );

  useEffect(() => {
    const initialNote = searchParams?.get('initialNote');

    if (initialNote) {
      setSelectedNote(thankYouNotesData?.docs?.find(note => note.id === initialNote));
    }
  }, [thankYouNotesData]);

  const handleRedirectToEmailClient = (email, message) => {
    window.location.href = encodeURI(
      `mailto:${email}?subject=${t('reYourThankYouNote')} 📬&body=\n\n${message}`
    );
  };

  const columns = React.useMemo(
    () => [
      {
        header: t('name'),
        size: 45,
        accessorKey: 'recipient',
        cell: ({ cell }) => {
          const value = cell?.getValue();
          const { firstName, lastName } = value || {};

          return <S.RecipientName>{`${firstName} ${lastName}`}</S.RecipientName>;
        }
      },
      {
        header: t('tyNote'),
        accessorKey: 'experience',
        cell: ({ cell }) => (
          <Typography
            variant="body2"
            {...getSafeInnerHtml(cell.getValue()?.thankYouMessage)}
            lineClamp={1}
          />
        )
      },
      {
        header: '',
        size: 45,
        accessorKey: 'reply',
        cell: ({ cell }) => {
          return (
            <Button
              onClick={() =>
                handleRedirectToEmailClient(
                  cell.row?.original?.recipient?.email,
                  convertHtmlMessageToString(cell?.row?.original?.experience?.thankYouMessage)
                )
              }
              variant="text"
              startIcon={<Icon name="email" />}
              disabled={!cell?.row?.original?.experience?.thankYouMessage}
            >
              {t('reply')}
            </Button>
          );
        }
      }
    ],
    [thankYouNotesData]
  );

  return (
    <>
      <S.Wrapper>
        <S.TableContainer>
          <Table
            columns={columns}
            data={thankYouNotesData?.docs || []}
            loading={thankYouNotesLoading}
            pageCount={thankYouNotesData?.totalPages}
            pagination={{
              pageIndex,
              pageSize,
              total: thankYouNotesData?.totalDocs
            }}
            manualPagination
            paginationProps={{ perPageSelection: true }}
            setPagination={setPagination}
            setGlobalFilters={setGlobalFilters}
            useGlobalFuzzyFilter
            rowHeight={52}
            onRowClick={row => setSelectedNote(row.original)}
            enabled={{
              filter: false,
              header: true
            }}
          />
        </S.TableContainer>
        {selectedNote ? (
          <S.MessageCardWrapper>
            <Card>
              <CardHeader>
                <Typography variant="subtitle4Bold">
                  {selectedNote.recipient.firstName} {selectedNote.recipient.lastName}
                </Typography>
                <OrderStatusBadge status={selectedNote?.giftStatus} />
              </CardHeader>
              <CardContent>
                <S.RecipientDetailButton
                  onClick={() => {
                    navigate(
                      ROUTE_PATHS.RECOGNITION_RECIPIENT_DETAIL(recognitionCode, selectedNote?.code)
                    );
                  }}
                >
                  <Icon name="avatar" space={5} />
                  {t('viewContactDetails').toUpperCase()}
                </S.RecipientDetailButton>

                <DetailItem title={`${t('email')}:`} withBorder>
                  {selectedNote.recipient?.email}
                </DetailItem>
                <DetailItem title={`${t('tyNote')}:`} withBorder>
                  <div {...getSafeInnerHtml(selectedNote.experience?.thankYouMessage)} />
                </DetailItem>
                <DetailItem title={`${t('giftMessage')}:`}>
                  {selectedNote.experience?.giftMessage && (
                    <div {...getSafeInnerHtml(selectedNote.experience?.giftMessage)} />
                  )}
                </DetailItem>
              </CardContent>
            </Card>
          </S.MessageCardWrapper>
        ) : (
          <S.InitialPreview>
            <Typography variant="body3Italic" color="gray">{`(${t(
              'thankYouNotesPreviewSubTitle'
            )})`}</Typography>
          </S.InitialPreview>
        )}
      </S.Wrapper>
    </>
  );
};
